import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { repositorioCctv } from '../../../data/repositories/RepositorioCctv';
import { useReproductorHls } from '../../hooks/useReproductorHls';
import estilos from './Panel.module.css';

interface FondoCamaraProps {
  camaraId: number;
}

/**
 * Una cámara en vivo como fondo del panel.
 *
 * Es la alternativa al plano fijo: en una instalación pequeña, ver la nave de
 * verdad con los sensores encima dice más que un dibujo. Los marcadores se
 * colocan igual, en porcentaje, así que siguen cuadrando aunque el vídeo tenga
 * otras proporciones que la imagen que hubiera antes.
 *
 * Si la cámara ya no existe —la borraron, o quien mira el panel dejó de tener
 * acceso a ella— se dice, en vez de dejar un rectángulo negro sin explicación.
 */
export function FondoCamara({ camaraId }: FondoCamaraProps) {
  const camaras = useQuery({
    queryKey: ['cctv', 'camaras', null],
    queryFn: () => repositorioCctv.listarCamaras(null),
    staleTime: 60_000,
  });

  const camara = camaras.data?.find((c) => c.id === camaraId) ?? null;

  /*
   * Cambia al pulsar el aviso y forma parte de la clave del reproductor, así
   * que React lo destruye y lo crea de cero.
   *
   * El vigilante ya rehace la sesión solo, pero puede tardar hasta medio
   * minuto entre intentos. Cuando alguien está mirando y ve la imagen parada,
   * no tiene por qué esperar: esto es el equivalente a recargar sólo el vídeo.
   */
  const [generacion, setGeneracion] = useState(0);

  if (camaras.isLoading) return null;

  if (!camara) {
    return (
      <div className={estilos.fondoVacio}>
        <div className={estilos.fondoVacioIcono}>📷</div>
        <div className={estilos.fondoVacioTitulo}>Esa cámara ya no está disponible</div>
        <div className={estilos.fondoVacioTexto}>
          La borraron o dejaste de tener acceso a ella. Entra en <b>Editar</b> para elegir otra.
        </div>
      </div>
    );
  }

  return (
    <Reproductor
      key={`${camara.enlaceHls}-${generacion}`}
      url={camara.enlaceHls}
      onReintentar={() => setGeneracion((n) => n + 1)}
    />
  );
}

const TEXTO: Record<string, string> = {
  conectando: 'Conectando…',
  reconectando: 'Reconectando…',
  sinSenal: 'Sin señal',
};

/**
 * El reproductor en sí, separado para poder rehacerlo entero con una clave.
 *
 * Recrear el componente es lo único que garantiza una sesión de vídeo nueva:
 * cambiar propiedades no basta, porque el reproductor vive fuera de React.
 */
function Reproductor({ url, onReintentar }: { url: string | null; onReintentar: () => void }) {
  const { video, estado } = useReproductorHls(url);

  return (
    <>
      <video ref={video} autoPlay muted playsInline className={estilos.videoFondo} />

      {/* Sólo se avisa cuando algo va mal; en marcha normal el vídeo habla solo. */}
      {!url && <span className={estilos.estadoFondo}>El servidor aún no publica esta cámara</span>}

      {url && estado !== 'enVivo' && (
        <button type="button" className={estilos.estadoFondo} onClick={onReintentar}>
          {TEXTO[estado] ?? 'Conectando…'} · reintentar
        </button>
      )}
    </>
  );
}
